import { PlusIcon, PreloaderIcon } from '../../../../common/ui/icons';
import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { insertProject, MENU_PROJECTS_QUERY_KEY } from '../../data/planner-data-service';
import { showToast } from '../../../../common/domain/toast-provider';
import { handleWiziwig } from '../../domain/wiziwig-provider';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IProjectsMenuResponse } from '../../planner-interfaces';

enum AddProjectMode {
    DEFAULT = 0,
    ADDING = 1,
}

const AddProject = () => {

    const { data: response } = useQuery<IProjectsMenuResponse>({
        queryKey: [MENU_PROJECTS_QUERY_KEY],
    });

    const [addProjectMode, setAddProjectMode] = useState(AddProjectMode.DEFAULT);
    const [title, setTitle] = useState('');
    const [titleTouched, setTitleTouched] = useState(false);

    const isTitleValid = !titleTouched || title.trim().length > 0;

    const titleRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        titleRef?.current?.focus();
    }, [addProjectMode]);

    const startAddingProject = () => {
        setAddProjectMode(AddProjectMode.ADDING);
    };

    const stopAddingProject = () => {
        setAddProjectMode(AddProjectMode.DEFAULT);
    };

    const onTitleChange = (evt: ChangeEvent<HTMLInputElement>) => {
        setTitle((evt.target as HTMLInputElement).value);
        setTitleTouched(true);
    };

    const queryClient = useQueryClient();

    const { isPending, mutate } = useMutation({
        mutationFn: (params: { project_title: string, project_order: number }) => {
            const { project_title, project_order} = params;
            return insertProject(project_title.trim(), project_order);
        },
        onSuccess: (response) => {
            queryClient.setQueryData([MENU_PROJECTS_QUERY_KEY], response);
            stopAddingProject();
        },
        onError: () => {
            showToast('Insert project error.');
        }
    });

    const save = async () => {
        if(title.trim().length <= 0) {
            setTitleTouched(true);
            return;
        }

        mutate({
            project_title: title.trim(),
            project_order: response?.data?.length || 0,
        });
    };

    const onKeyDown = async (evt: KeyboardEvent<HTMLInputElement>) => {
        if(handleWiziwig(evt, titleRef, (newText) => {
            setTitle(newText);
            setTitleTouched(true);
        })){
            return;
        }

        if(evt.code === 'Escape') {
            stopAddingProject();
        }

        if(evt.code === 'Enter') {
            await save();
        }
    };

    return (
        <>
            {
                addProjectMode === AddProjectMode.DEFAULT &&
                <button
                    onClick={ startAddingProject }
                    type="button"
                    className="flex items-center text-sm mt-4 mb-10 text-slate-400">
                    <div className="mr-3"><PlusIcon /></div>
                    <div>Add Project</div>
                </button>
            }

            {
                addProjectMode === AddProjectMode.ADDING &&
                <div className="mt-4 mb-10">
                    <input
                        ref={ titleRef }
                        onInput={ onTitleChange }
                        onKeyDown={ onKeyDown }
                        className={ `bg-slate-100 text-slate-900 w-full rounded px-2 py-1 outline-none ${ isTitleValid ? '' : 'border-2 border-red-500' }` }
                        type="text"
                    />

                    {
                        !isTitleValid && <div className="text-red-500 text-xs mt-1">The title is required.</div>
                    }

                    <div className="flex items-center justify-end text-sm mt-4">
                        <button
                            className="bg-stone-400 text-slate-100 rounded px-4 py-2 mr-2"
                            onClick={ stopAddingProject }
                            type="button">Cancel</button>

                        {
                            isPending &&
                            <PreloaderIcon size={ 24 } color={ '#717985' } />
                        }

                        {
                            !isPending &&
                            <button
                                className="bg-slate-500 text-slate-100 rounded px-6 py-2"
                                onClick={ save }
                                type="button">Save</button>
                        }
                    </div>
                </div>
            }
        </>
    )
};

export default AddProject;